import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from '../api/client';

// Синтетический id серверного /admin cookie-канала (гибрид CU-869dt3dz6):
// сам канал admin_password остаётся нетронутым, но React-приложение теперь
// авторизуется по локальному JWT (email+password), заменившему Keycloak.
export const DASHBOARD_ADMIN_ID = 'dashboard-admin';

type AuthMode = 'dashboard' | 'embed' | 'anonymous';

interface AuthState {
  mode: AuthMode;
  userId: string | null;
  email: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  // Платформенный канал (Hub-embed, CU-869e4mvzp): refresh-токен aseller.io из INIT
  // и полученный на него access. Держим отдельно от локальных токенов — источник,
  // время жизни и способ обновления у них разные, а persist их не касается вовсе.
  platformAccessToken: string | null;
  platformRefreshToken: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: (identifier: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  applyTokens: (accessToken: string, refreshToken: string) => void;
  applyPlatformIdentity: (params: {
    refreshToken: string;
    accessToken: string;
    userId?: string | null;
  }) => void;
  setPlatformAccessToken: (accessToken: string) => void;
  clearPlatformAuth: () => void;
  resetAuth: () => void;
}

const CLEARED = {
  mode: 'anonymous' as AuthMode,
  userId: null,
  email: null,
  accessToken: null,
  refreshToken: null,
  platformAccessToken: null,
  platformRefreshToken: null,
  isAuthenticated: false,
  isAdmin: false,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...CLEARED,
      isLoading: true,

      login: async (identifier: string, password: string) => {
        try {
          if (identifier.includes('@')) {
            const { data } = await apiClient.post('/api/auth/login', {
              email: identifier,
              password,
            });
            set({ accessToken: data.access_token, refreshToken: data.refresh_token });
          } else {
            await apiClient.post('/api/admin/login', {
              username: identifier,
              password,
            });
            set({ accessToken: null, refreshToken: null });
          }
          await get().fetchMe();
          return get().isAuthenticated;
        } catch {
          return false;
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        if (refreshToken) {
          try {
            await apiClient.post('/api/auth/logout', { refresh_token: refreshToken });
          } catch { /* idempotent — даже невалидный токен это success */ }
        } else if (get().mode === 'dashboard') {
          try {
            await apiClient.post('/api/admin/logout');
          } catch { /* локальное состояние всё равно очищаем */ }
        }
        set({ ...CLEARED, isLoading: false });
      },

      // Валидация сессии при старте: если access протух — client-интерсептор
      // сам обновит его по refresh-токену и повторит запрос. Запрос также
      // восстанавливает admin_users-сессию из HttpOnly admin_token cookie.
      fetchMe: async () => {
        try {
          const { data } = await apiClient.get('/api/auth/me');
          set({
            mode: 'dashboard',
            userId: String(data.user_id),
            email: data.email ?? null,
            isAdmin: !!data.is_admin,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          set({ ...CLEARED, isLoading: false });
        }
      },

      applyTokens: (accessToken: string, refreshToken: string) => {
        set({ accessToken, refreshToken });
      },

      // Hub-embed: личность приходит от платформы, а не от логина модуля.
      // isAdmin намеренно остаётся false — админ-UI (управление пользователями
      // idea_calc) в iframe не показываем.
      applyPlatformIdentity: ({ refreshToken, accessToken, userId }) => {
        set({
          mode: 'embed',
          userId: userId ?? null,
          platformRefreshToken: refreshToken,
          platformAccessToken: accessToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      setPlatformAccessToken: (accessToken: string) => {
        set({ platformAccessToken: accessToken });
      },

      clearPlatformAuth: () => {
        set({
          platformAccessToken: null,
          platformRefreshToken: null,
          ...(get().mode === 'embed'
            ? { mode: 'anonymous' as AuthMode, isAuthenticated: false, userId: null }
            : {}),
        });
      },

      resetAuth: () => set({ ...CLEARED, isLoading: false }),
    }),
    {
      name: 'idea-calc-auth',
      // Персистим только локальные токены — профиль (isAdmin/email) перечитываем
      // через /api/auth/me на старте, чтобы не доверять устаревшему localStorage.
      // Платформенные токены НЕ персистим: refresh-токен aseller.io приходит от
      // Hub'а в INIT при каждой загрузке iframe, класть его в localStorage незачем.
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);
